import React, { createContext, Dispatch, SetStateAction, useContext, useState } from "react";
import { getAffiliationsByOrgId, getAllAffiliations } from "../api/AffiliateApi";
import { getClubsByOrgStatsId, getMajorClubs } from "../api/ClubApi";
import { BigsClub } from "../types/personnel";

type AffiliationContext = {
    affiliationDropdownOptions: Array<AffiliationDropdownOptionType>;
    setAffiliationDropdownOptions: Dispatch<SetStateAction<AffiliationDropdownOptionType[]>>;
    refreshAffiliationDropdownByOrgId: (orgId: number) => void;
    refreshAffiliationsDropdownAllOrgs: () => void;
    /** Club pickers: teams for an org from {@code bigs.club} (Stats team id as value). */
    refreshClubDropdownByOrgStatsId: (orgStatsId: number) => void;
    /** Club pickers: major clubs from {@code bigs.club}. */
    refreshClubDropdownAllMajors: () => void;
};

type AffiliationDropdownOptionType = {
    value: number;
    label: string;
    teamId: number;
    orgId: number;
    clubStatsId?: number;
};

const clubToDropdownOption = (club: BigsClub): AffiliationDropdownOptionType => {
    const level = club.level ? club.level.trim() : "";
    const label =
        level === "MLB" ? `${club.name} (Major)` : level ? `${club.name} (${level})` : club.name;
    return {
        value: club.clubStatsId,
        label,
        teamId: club.clubStatsId,
        orgId: club.orgStatsId,
        clubStatsId: club.clubStatsId,
    };
};

const AffiliationContext = createContext<AffiliationContext>({
    affiliationDropdownOptions: [],
    setAffiliationDropdownOptions: null,
    refreshAffiliationDropdownByOrgId: null,
    refreshAffiliationsDropdownAllOrgs: null,
    refreshClubDropdownByOrgStatsId: null,
    refreshClubDropdownAllMajors: null,
});

const AffiliationContextProvider: React.FC = ({ children }) => {
    const [affiliationDropdownOptions, setAffiliationDropdownOptions] = useState<
        AffiliationDropdownOptionType[]
    >([]);

    const refreshAffiliationDropdownByOrgId = (orgId: number) => {
        getAffiliationsByOrgId(orgId).then(response => {
            setAffiliationDropdownOptions(
                response
                    .map((affiliate: any) => {
                        return {
                            value: affiliate.id,
                            label:
                                affiliate.level === "MLB"
                                    ? affiliate.name + " (Major)"
                                    : affiliate.name + " (" + affiliate.level.trim() + ")",
                            teamId: affiliate.teamId,
                            orgId: affiliate.orgId,
                            ...affiliate,
                        };
                    })
                    .sort((a: any, b: any) => a.label.localeCompare(b.label)),
            );
        });
    };

    const refreshAffiliationsDropdownAllOrgs = () => {
        getAllAffiliations().then(response => {
            if (response) {
                const filteredResponse = response
                    .map((club: { name: any; id: any; orgId: any }) => {
                        return {
                            label: club.name,
                            value: club.id,
                            orgId: club.orgId,
                            ...club,
                        };
                    })
                    .sort((a: any, b: any) => a.name.localeCompare(b.name));
                setAffiliationDropdownOptions(filteredResponse);
            }
        });
    };

    const refreshClubDropdownByOrgStatsId = (orgStatsId: number) => {
        getClubsByOrgStatsId(orgStatsId)
            .then((response: BigsClub[]) => {
                const options = (response || [])
                    .map(clubToDropdownOption)
                    .sort((a, b) => a.label.localeCompare(b.label));
                setAffiliationDropdownOptions(options);
            })
            .catch(() => {
                setAffiliationDropdownOptions([]);
            });
    };

    const refreshClubDropdownAllMajors = () => {
        getMajorClubs()
            .then((response: BigsClub[]) => {
                const options = (response || [])
                    .map(clubToDropdownOption)
                    .sort((a, b) => a.label.localeCompare(b.label));
                setAffiliationDropdownOptions(options);
            })
            .catch(() => {
                setAffiliationDropdownOptions([]);
            });
    };

    return (
        <AffiliationContext.Provider
            value={{
                affiliationDropdownOptions,
                setAffiliationDropdownOptions,
                refreshAffiliationDropdownByOrgId,
                refreshAffiliationsDropdownAllOrgs,
                refreshClubDropdownByOrgStatsId,
                refreshClubDropdownAllMajors,
            }}
        >
            {children}
        </AffiliationContext.Provider>
    );
};

const useAffiliation = (): AffiliationContext => {
    const context = useContext<AffiliationContext>(AffiliationContext);
    if (context === undefined) {
        throw new Error(`useAffiliation must be used within a AffiliationContextProvider`);
    }
    return context;
};

export { AffiliationContextProvider, AffiliationDropdownOptionType, useAffiliation };
